// Relative imports (not `@/`) so bun can test this module.
import { getHexTileMapKey } from '../hexmap/model/keys';
import { getTile } from '../hexmap/model/map';
import type { HexTileMap } from '../hexmap/model/types';
import type { RuleSet } from '../rules';
import { unitGlyph } from '../rules/ui';
import {
  type Building,
  type Cell,
  type Command,
  type EntityId,
  gatherResource,
  type PlayerId,
  type SimState,
  territoryOwners,
  type Unit,
  workerSlots,
} from '../sim';
import { assignedWorkers, type WorkerIndex, workerIndex } from './activity';

/** How a tile type reads on screen (the tile info panel, a worker's wild target). */
export const TILE_NAMES: Record<string, string> = {
  forest: 'Forest',
  field: 'Field',
  beach: 'Beach',
  water: 'Water',
  default: 'Grass',
};

/**
 * What each own worker is up to, for the selection dock, the dashed intent
 * lines and the Pinned chip. Own units and buildings only (a worker's job is
 * never in the fog: the viewer's state holds all of its owner's), wild tiles
 * named by their terrain.
 */
export interface IntentCtx {
  /** The viewer's state (`Frame.view`). */
  state: SimState;
  rules: RuleSet;
  terrain: HexTileMap;
  playerId: PlayerId;
  cityNumbers: ReadonlyMap<string, number>;
  /** This tick's `workerIndex` (`tickWorkers`), else computed on first use. */
  index?: WorkerIndex;
}

export interface WorkerIntent {
  id: EntityId;
  /** "Wk 4": the type's roster tag and the unit's number. */
  label: string;
  /** Held on its job by `assign` (`order.pin`). */
  pinned: boolean;
  /** The home city's name ("City 1"), null with none. */
  home: string | null;
  /** "Gathering food", "Building", "Hauling food", "Idle". */
  verb: string;
  /** Where it works: "Farm (4/5)", "Forest", "Barracks site 62%"; null when idle. */
  where: string | null;
  /** The tile it works or is heading for, for the map line. */
  target: Cell | null;
  /** Working in the territory of a city other than its home. */
  visiting: boolean;
}

type Job =
  | { kind: 'gather'; at: Cell; pinned: boolean }
  | { kind: 'haul'; at: Cell | null; pinned: boolean }
  | { kind: 'build'; at: Cell; pinned: boolean }
  | { kind: 'idle'; moving: boolean; pinned: boolean };

const sameCell = (a: Cell, b: Cell) => a.q === b.q && a.r === b.r;

/** Whether `unit` is held on a job by `assign`. */
export function isPinned(unit: Unit): boolean {
  const order = unit.order;
  return (order?.kind === 'gather' || order?.kind === 'build') && order.pin === true;
}

/** Whether `unit` is an own worker (automates to work the land). */
export function isWorker(rules: RuleSet, unit: Unit, playerId: PlayerId): boolean {
  return unit.owner === playerId && rules.units[unit.type]?.automate === 'work';
}

/** First resource id (sorted, as the sim picks) a wild tile type yields. */
function tileResource(rules: RuleSet, terrain: HexTileMap, cell: Cell): string | null {
  const tile = getTile(terrain, cell);
  if (!tile) return null;
  for (const id of Object.keys(rules.resources).sort()) {
    if ((rules.resources[id]?.gatheredFrom as readonly string[] | undefined)?.includes(tile.type)) {
      return id;
    }
  }
  return null;
}

/** An intent reader for this tick; the building-by-tile map is built once. */
export function intentReader(ctx: IntentCtx): (unit: Unit) => WorkerIntent | null {
  const { state, rules, terrain, playerId } = ctx;
  const covering = new Map<string, Building>();
  for (const b of Object.values(state.buildings)) {
    if (b.owner !== playerId) continue;
    covering.set(getHexTileMapKey(b.pos), b);
    for (const cell of b.footprint ?? []) covering.set(getHexTileMapKey(cell), b);
  }
  let index = ctx.index;
  let owners: ReturnType<typeof territoryOwners> | undefined;
  const simCtx = { terrain, rules };
  const resName = (res: string) => (rules.resources[res]?.name ?? res).toLowerCase();

  function jobOf(unit: Unit): Job {
    const order = unit.order;
    if (order?.kind === 'gather' && order.pin) {
      return order.haul
        ? { kind: 'haul', at: order.target, pinned: true }
        : { kind: 'gather', at: order.target, pinned: true };
    }
    if (order?.kind === 'build' && order.pin) {
      const site = state.buildings[order.buildingId];
      return site
        ? { kind: 'build', at: site.pos, pinned: true }
        : { kind: 'idle', moving: false, pinned: true };
    }
    if (order) return { kind: 'idle', moving: order.kind === 'move', pinned: false };
    const auto = unit.auto;
    if (auto?.phase === 'haul') return { kind: 'haul', at: auto.work ?? null, pinned: false };
    if (auto?.target) {
      const b = covering.get(getHexTileMapKey(auto.target));
      return b && b.buildProgress < 1
        ? { kind: 'build', at: auto.target, pinned: false }
        : { kind: 'gather', at: auto.target, pinned: false };
    }
    // Standing where it works: inside its farm, or on a wild tile it gathers.
    if (unit.inside || gatherResource(state, simCtx, unit)) {
      return { kind: 'gather', at: unit.pos, pinned: false };
    }
    return { kind: 'idle', moving: false, pinned: false };
  }

  function whereText(at: Cell): string {
    const b = covering.get(getHexTileMapKey(at));
    if (b) {
      const name = rules.buildings[b.type]?.name ?? b.type;
      if (b.buildProgress < 1) return `${name} site ${Math.floor(b.buildProgress * 100)}%`;
      if (!rules.buildings[b.type]?.provides) return name;
      index ??= workerIndex(state, rules);
      return `${name} (${assignedWorkers(state, rules, b, index).total}/${workerSlots(rules, b)})`;
    }
    const type = getTile(terrain, at)?.type;
    return type ? (TILE_NAMES[type] ?? type) : 'Unknown';
  }

  function resourceAt(at: Cell): string | null {
    const b = covering.get(getHexTileMapKey(at));
    if (b) return rules.buildings[b.type]?.provides?.resource ?? null;
    return tileResource(rules, terrain, at);
  }

  return (unit) => {
    if (!isWorker(rules, unit, playerId)) return null;
    const home = state.buildings[unit.homeCityId ?? ''];
    const homeCity = home?.owner === playerId ? home : undefined;
    const job = jobOf(unit);
    const label = `${unitGlyph(rules, unit.type)} ${unit.id.slice(1)}`;
    const base = {
      id: unit.id,
      label,
      home: homeCity ? `City ${ctx.cityNumbers.get(homeCity.id) ?? '?'}` : null,
    };
    if (job.kind === 'idle') {
      return {
        ...base,
        // A pinned order whose site is gone still holds the worker (and its badge).
        pinned: job.pinned,
        verb: job.moving ? 'Moving' : 'Idle',
        where: null,
        target: null,
        visiting: false,
      };
    }
    const at = job.at;
    const res =
      job.kind === 'haul' ? unit.carry?.res : job.kind === 'gather' ? resourceAt(job.at) : null;
    owners ??= territoryOwners(state, rules);
    const workCity = at ? owners.get(getHexTileMapKey(at)) : undefined;
    return {
      ...base,
      pinned: job.pinned,
      verb:
        job.kind === 'build'
          ? 'Building'
          : `${job.kind === 'haul' ? 'Hauling' : 'Gathering'}${res ? ` ${resName(res)}` : ''}`,
      where: at ? whereText(at) : null,
      target: at,
      visiting:
        !!homeCity &&
        !!workCity &&
        workCity !== homeCity.id &&
        state.buildings[workCity]?.owner === playerId,
    };
  };
}

/** The dashed map lines for `units`: from each worker to its job's tile (none once there). */
export function intentLines(
  ctx: IntentCtx,
  units: readonly Unit[]
): Array<{ id: EntityId; from: Cell; to: Cell }> {
  const read = intentReader(ctx);
  const lines: Array<{ id: EntityId; from: Cell; to: Cell }> = [];
  for (const unit of units) {
    const target = read(unit)?.target;
    if (target && !sameCell(unit.pos, target))
      lines.push({ id: unit.id, from: unit.pos, to: target });
  }
  return lines;
}

/**
 * The tile a worker is on the job at, for the Pinned chip: the work tile it
 * hauls from or heads for, else the one it works where it stands. Null when
 * it is already pinned or has no job.
 */
export function pinTarget(ctx: IntentCtx, unit: Unit): Cell | null {
  if (!isWorker(ctx.rules, unit, ctx.playerId) || unit.order) return null;
  const { auto } = unit;
  if (auto?.phase === 'haul') return auto.work ?? null;
  if (auto?.target) return auto.target;
  if (unit.inside || gatherResource(ctx.state, { terrain: ctx.terrain, rules: ctx.rules }, unit)) {
    return unit.pos;
  }
  return null;
}

/** `assign` (pinned) commands holding each worker in `units` on its current job, one per tile. */
export function pinCommands(ctx: IntentCtx, units: readonly Unit[]): Command[] {
  const byTile = new Map<string, { target: Cell; unitIds: EntityId[] }>();
  for (const unit of units) {
    const target = pinTarget(ctx, unit);
    if (!target) continue;
    const key = getHexTileMapKey(target);
    const entry = byTile.get(key) ?? { target: { q: target.q, r: target.r }, unitIds: [] };
    entry.unitIds.push(unit.id);
    byTile.set(key, entry);
  }
  return [...byTile.values()].map((e) => ({
    type: 'assign',
    unitIds: e.unitIds,
    target: e.target,
    pin: true,
  }));
}
